/**
 * Home View completa — agrupa as ilhas estáticas da tela inicial:
 *   - HeaderIsland (sticky vermelho)
 *   - HighlightsCarousel (carrossel destaques)
 *   - CategoryPillsIsland (pills sticky)
 *   - ProductCardsIsland (categorias inline com cards + stepper)
 *
 * Lê MENU_DATA do window (V1 ainda dono do fetch). Re-renderiza quando
 * V1 dispara `aliv:home-rendered` (ex: após processPromotionalMenu).
 *
 * V1 segue dono de:
 *   - Wizard de produto (pizza/combo/simples — click no card chama)
 *   - Modal de preview (delegado pra ProductPreviewIsland via evento)
 *   - Cart, login, checkout
 *
 * Toda essa árvore é renderizada DENTRO do `#home-view` do V1, que vira
 * um container vazio quando V2 owns. O `renderHome()` do V1 não mexe
 * mais nele (early return).
 *
 * DUAS FORMAS, UMA ÁRVORE. A partir de 1024px entra a barra lateral fixa
 * (`DesktopSidebar`) + barra de busca no topo (`DesktopTopBar`), e as
 * pílulas horizontais de categoria saem — a lista da lateral já faz o
 * mesmo papel com mais folga. Tudo por classe `lg:` do Tailwind: o celular
 * renderiza exatamente a mesma árvore de antes, sem branch de JS, sem
 * `window.innerWidth`, e portanto sem risco de escolher a forma errada
 * na hidratação. Ver `project_delivery_app_idiomas_e_desktop.md`.
 */
import { useEffect, useState } from 'react';
import type { MenuCategory } from '../types';
import { HeaderIsland } from './HeaderIsland';
import { HighlightsCarousel } from './HighlightsCarousel';
import { CategoryPillsIsland } from './CategoryPillsIsland';
import { ProductCardsIsland } from './ProductCardsIsland';
import { DesktopSidebar } from './DesktopSidebar';
import { DesktopTopBar } from './DesktopTopBar';
import { useI18n } from '../i18n';

declare global {
  interface Window {
    // MENU_DATA vem do global.d.ts (union de Array delivery + Object admin).
  }
}

interface HomeViewIslandProps {
  storeName: string;
  storeLogo: string | null;
  bannerUrl: string | null;
  bannerIsVideo: boolean;
  isOpen: boolean;
  deliveryEstimate: string;
  minOrderValue: number;
}

export function HomeViewIsland({
  storeName,
  storeLogo,
  bannerUrl,
  bannerIsVideo,
  isOpen,
  deliveryEstimate,
  minOrderValue,
}: HomeViewIslandProps) {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  // Re-render quando V1 atualiza MENU_DATA (após fetch /api/menu/<slug>
  // ou após processPromotionalMenu()).
  const { t } = useI18n();
  const [tick, setTick] = useState(0);
  const [menuFailed, setMenuFailed] = useState(false);
  useEffect(() => {
    const onUpdate = () => { setMenuFailed(false); setTick((t) => t + 1); };
    const onFailed = () => setMenuFailed(true);
    window.addEventListener('aliv:home-rendered', onUpdate);
    window.addEventListener('aliv:menu-load-failed', onFailed);
    // FECHA A RACE render→effect: o loadMenu() do boot inline dispara o fetch
    // ANTES deste componente montar. Se ele resolver e emitir
    // `aliv:home-rendered` na janela ENTRE o 1º render e este useEffect, o
    // evento é perdido — MENU_DATA fica setado mas o React não re-renderiza,
    // deixando o skeleton ETERNO. Aconteceu no WebView do Instagram (timing de
    // JS/paint mais lento que browser normal cai nessa janela; browser rápido
    // não). Re-checa aqui: se o menu já chegou, força 1 render agora.
    if (((window.MENU_DATA as MenuCategory[] | undefined)?.length ?? 0) > 0) {
      setTick((t) => t + 1);
    }
    return () => {
      window.removeEventListener('aliv:home-rendered', onUpdate);
      window.removeEventListener('aliv:menu-load-failed', onFailed);
    };
  }, []);

  const categories = ((window.MENU_DATA as MenuCategory[] | undefined) || []) as MenuCategory[];
  // Memo seria overkill — re-render é raro (só quando MENU_DATA muda),
  // e o set de categories é pequeno (15-30 items).
  void tick;

  return (
    // pb-24 = espaço do BottomNav no celular; no computador ele não existe,
    // fica só um respiro curto depois do rodapé.
    <div className="pb-24 lg:pb-6 lg:flex lg:items-start">
      <DesktopSidebar
        categories={categories}
        storeName={storeName}
        storeLogo={storeLogo}
        isOpen={isOpen}
        deliveryEstimate={deliveryEstimate}
      />
      {/* `min-w-0` é obrigatório: sem ele o filho flex adota a largura do
          conteúdo mais largo (nome de produto comprido) e empurra a lateral
          pra fora da tela. */}
      <div className="flex-1 min-w-0">
      <HeaderIsland
        storeName={storeName}
        storeLogo={storeLogo}
        bannerUrl={bannerUrl}
        bannerIsVideo={bannerIsVideo}
        isOpen={isOpen}
        deliveryEstimate={deliveryEstimate}
        minOrderValue={minOrderValue}
      />
      <DesktopTopBar />
      {categories.length > 0 && <HighlightsCarousel />}
      {/* Pílulas só no celular — no computador a lista fixa da lateral já
          navega, e as duas juntas seriam o mesmo menu duas vezes. */}
      {categories.length > 0 && (
        <div className="lg:hidden">
          <CategoryPillsIsland categories={categories} />
        </div>
      )}
      {categories.length === 0 && menuFailed ? (
        // Recuperação: o menu FALHOU depois dos retries (loadMenu dispara
        // `aliv:menu-load-failed`). Em vez de skeleton eterno, oferece recarregar
        // e abrir no navegador — o WebView do Instagram/Facebook às vezes engasga
        // no fetch e o cliente confirmou que no navegador normal carrega tudo.
        <div className="p-6 pt-10 flex flex-col items-center text-center gap-3">
          <i className="fas fa-triangle-exclamation text-3xl text-amber-500" />
          <p className="text-sm font-semibold text-gray-700">
            {t('Não conseguimos carregar o cardápio agora.')}
          </p>
          <p className="text-xs text-gray-500 -mt-1">
            {t('Se você abriu por um app (Instagram/Facebook), tente abrir no navegador.')}
          </p>
          <div className="flex gap-2 mt-1">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold"
            >
              {t('Tentar de novo')}
            </button>
            <button
              onClick={() => { try { window.open(window.location.href, '_blank'); } catch { window.location.href = window.location.href; } }}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-bold border border-gray-200"
            >
              {t('Abrir no navegador')}
            </button>
          </div>
        </div>
      ) : categories.length === 0 ? (
        <div className="p-6 space-y-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex justify-between items-start animate-pulse"
            >
              <div className="flex-1 pr-2 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/5" />
                <div className="h-3 bg-gray-100 rounded w-4/5" />
                <div className="h-3 bg-gray-100 rounded w-2/5" />
                <div className="h-6 bg-gray-200 rounded w-24" />
              </div>
              <div className="w-24 h-24 bg-gray-100 rounded-xl ml-3 shrink-0" />
            </div>
          ))}
        </div>
      ) : (
        <ProductCardsIsland categories={categories} />
      )}
      {/* Assinatura da plataforma no fim do cardápio — só o nome, SEM link
          (dono, 20/10/2026): o cardápio é o maior meio de acesso, mas um
          link aqui tiraria o cliente da loja no meio do pedido. */}
      {categories.length > 0 && (
        <p className="pt-6 pb-2 text-center text-[11px] text-gray-400 select-none">
          {t('Desenvolvido por')} <span className="font-semibold text-gray-500">Aliv Hub</span>
        </p>
      )}
      </div>
    </div>
  );
}
